import React, { useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage, ChatMessageMetadata } from '@/features/chat/types/chat';
import LexicalMessageRenderer from './LexicalMessageRenderer';
import EntityRenderer, { Entity } from './entities/EntityRenderer';
import InlineWorkflowWidget, { Widget } from './InlineWorkflowWidget';
import { MessageDensityMode } from './MessageDensityToggle';
import LexFeedbackButtons from './LexFeedbackButtons';
import { useChatStore } from '@/store/chatStore';
import { useKeycloak } from '@react-keycloak/web';

interface ChatBubbleProps {
  message: ChatMessage;
  onRetry?: () => void;
  density?: MessageDensityMode;
  onEntityClick?: (entity: Entity) => void;
  onEntityHover?: (entity: Entity) => void;
  onWidgetAction?: (widgetId: string, action: string, data?: any) => void;
  widgets?: Widget[];
  className?: string;
  /** The user question that triggered this response (for feedback) */
  userQuestion?: string;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  onRetry,
  density = 'spacious',
  onEntityClick,
  onEntityHover,
  onWidgetAction,
  widgets,
  className = '',
  userQuestion,
}) => {
  const { keycloak } = useKeycloak();
  const [isVisible, setIsVisible] = useState(false);
  const [showTimestamp, setShowTimestamp] = useState(false);
  
  const isUser = message.role === 'user';
  const isError = message.status === 'error';
  const isStreaming = message.status === 'streaming';
  const isCompact = density === 'compact';
  const metadata = ((message.metadata || {}) as ChatMessageMetadata);
  const agenticMeta = metadata.agenticEvaluation;
  const personaMeta = metadata.persona;

  // Animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Handle entity interactions
  const handleEntityClick = useCallback((entity: Entity) => {
    onEntityClick?.(entity);
    
    // Trigger entity action event for right panel
    const event = new CustomEvent('entityAction', {
      detail: {
        entity,
        action: {
          action: entity.type === 'client' ? 'view_client' : 
                  entity.type === 'date' ? 'add_to_calendar' : 'view_details',
          target: entity.type
        }
      }
    });
    window.dispatchEvent(event);
  }, [onEntityClick]);

  const handleEntityHover = useCallback((entity: Entity) => {
    onEntityHover?.(entity);
  }, [onEntityHover]);

  // Handle widget interactions
  const handleWidgetAction = useCallback((widgetId: string, action: string, data?: any) => {
    onWidgetAction?.(widgetId, action, data);
  }, [onWidgetAction]);

  // Get user avatar
  const getUserAvatar = () => {
    if (isUser) {
      const token = keycloak.tokenParsed as Record<string, unknown>;
      const firstName = token?.given_name as string;
      const lastName = token?.family_name as string;
      
      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      }
      return 'U';
    }
    return 'L'; // Lex avatar
  };

  // Dynamic spacing based on density
  const messageSpacing = isCompact ? 'mb-3' : 'mb-6';
  const avatarSize = isCompact ? 'w-8 h-8' : 'w-10 h-10';
  const bubblePadding = isCompact ? 'p-3' : 'p-4';
  const fontSize = isCompact ? 'text-sm' : 'text-base';
  const lineHeight = isCompact ? 'leading-5' : 'leading-6';

  // Enhanced bubble styling
  const bubbleClasses = [
    'relative rounded-2xl shadow-sm border transition-all duration-300',
    bubblePadding,
    fontSize,
    lineHeight,
    isUser 
      ? 'bg-lex-accent text-lex-accent-contrast border-lex-accent rounded-br-md ml-auto max-w-2xl'
      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 rounded-bl-md mr-auto max-w-4xl',
    isError && 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100',
    isStreaming && 'bg-lex-accent-soft border-lex-accent',
    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
  ].filter(Boolean).join(' ');

  // Avatar styling
  const avatarClasses = [
    'flex items-center justify-center rounded-full font-semibold flex-shrink-0',
    avatarSize,
    isCompact ? 'text-xs' : 'text-sm',
    isUser 
      ? 'bg-lex-accent-hover text-lex-accent-contrast'
      : 'bg-gradient-to-br from-lex-accent to-lex-accent-hover text-lex-accent-contrast'
  ].join(' ');

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${messageSpacing} ${className}`}>
      <div className={`flex gap-3 max-w-5xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={avatarClasses}>
          {getUserAvatar()}
        </div>

        {/* Message content */}
        <div className="flex-1 min-w-0">
          <div className={bubbleClasses}>
            {/* Streaming indicator */}
            {isStreaming && (
              <div className="flex items-center space-x-1 mb-2">
                <div className="w-2 h-2 bg-lex-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-lex-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-lex-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                <span className="text-sm text-lex-accent ml-2">Lex is thinking...</span>
              </div>
            )}

            {/* Message content with entity rendering */}
            <div className="w-full">
              {message.renderType === 'markdown' && typeof message.content === 'string' ? (
                <div className={`prose prose-sm max-w-none dark:prose-invert ${isUser ? 'prose-invert' : ''}`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children, ...props }) => {
                        const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);
                        return (
                          <a
                            href={href}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            className="text-lex-accent underline underline-offset-2 hover:opacity-90"
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : typeof message.content === 'string' ? (
                <EntityRenderer
                  text={message.content}
                  entities={message.entities}
                  onEntityClick={handleEntityClick}
                  onEntityHover={handleEntityHover}
                  compact={isCompact}
                  className={isUser ? 'text-white' : 'text-gray-900 dark:text-white'}
                />
              ) : (
                <LexicalMessageRenderer initialText={String(message.content)} entities={message.entities} />
              )}
            </div>

            {!isUser && (agenticMeta?.enhanced || personaMeta?.styleMode) && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {agenticMeta?.enhanced && (
                  <span className="inline-flex items-center rounded-full border border-lex-accent bg-lex-accent-soft px-2 py-1 text-lex-accent">
                    Agentic Enhanced
                  </span>
                )}
                {typeof agenticMeta?.confidence === 'number' && (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
                    Confidence {(agenticMeta.confidence * 100).toFixed(0)}%
                  </span>
                )}
                {personaMeta?.styleMode && (
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    Style {personaMeta.styleMode}
                  </span>
                )}
              </div>
            )}

            {/* Error retry button */}
            {isError && onRetry && (
              <button 
                onClick={onRetry}
                className="mt-3 px-4 py-2 text-sm bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/50 transition-colors font-medium"
              >
                Retry Message
              </button>
            )}
          </div>

          {/* Inline workflow widgets */}
          {widgets && widgets.length > 0 && (
            <div className="mt-3 space-y-2">
              {widgets.map((widget) => (
                <InlineWorkflowWidget
                  key={widget.id}
                  widget={widget}
                  onTaskToggle={(taskId, completed) => handleWidgetAction(widget.id, 'toggle_task', { taskId, completed })}
                  onWorkflowAction={(action) => handleWidgetAction(widget.id, 'workflow_action', { action })}
                  onExpand={(widgetId, expanded) => handleWidgetAction(widgetId, 'expand', { expanded })}
                  compact={isCompact}
                />
              ))}
            </div>
          )}

          {/* Timestamp */}
          <div 
            className={`mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 transition-opacity duration-200 ${
              showTimestamp ? 'opacity-100' : 'opacity-0 hover:opacity-100'
            }`}
            onMouseEnter={() => setShowTimestamp(true)}
            onMouseLeave={() => setShowTimestamp(false)}
          >
            <span>
              {message.createdAt 
                ? new Date(message.createdAt).toLocaleTimeString() 
                : 'Now'
              }
            </span>
            {message.status === 'delivered' && (
              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
            )}
            {message.status === 'error' && (
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
            )}
          </div>

          {/* Feedback buttons for assistant messages */}
          {!isUser && !isStreaming && typeof message.content === 'string' && message.content.length > 0 && (
            <FeedbackRow
              messageId={message.id}
              userQuestion={userQuestion}
              responseText={message.content}
              intentCategory={metadata?.intentCategory as string | undefined}
              toolId={metadata?.toolId as string | undefined}
              toolSuccess={metadata?.toolSuccess as boolean | undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/** Tiny wrapper that reads conversationId from the store and passes pipeline metadata */
const FeedbackRow: React.FC<{
  messageId: string;
  userQuestion?: string;
  responseText: string;
  intentCategory?: string;
  toolId?: string;
  toolSuccess?: boolean;
}> = ({ messageId, userQuestion, responseText, intentCategory, toolId, toolSuccess }) => {
  const conversationId = useChatStore((s) => s.currentConversationId);
  if (!conversationId || !userQuestion) return null;
  return (
    <LexFeedbackButtons
      conversationId={conversationId}
      messageId={messageId}
      userQuestion={userQuestion}
      responseText={responseText}
      intentCategory={intentCategory}
      toolId={toolId}
      toolSuccess={toolSuccess}
    />
  );
};

export default ChatBubble;
